import React from 'react'
import classNames from 'classnames'
import { Button, Image, GridItem } from '@deal/components'
import { ProductExpertCardFragment } from '#src/generated/graphql'
import { ExpertCalloutPlacement } from '../'
import styles from './styles.css'
import { Link } from 'react-router-dom'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { generateCreateUrlFromCategory } from '#src/services/path'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import ExpertChatButton from '#src/components/ExpertChatButton'
import numeral from 'numeral'
import Check from './check.svg'
import Star from './star.svg'
import Map from './map.svg'
import Comment from './comment.svg'

interface V2Props {
  placement: ExpertCalloutPlacement
  expertCalloutExpert: ProductExpertCardFragment
  categorySlug: string
  swap?: boolean
  headerClassName?: string
}

const V2: React.SFC<V2Props & WithAnalyticsProps> = ({
  placement,
  expertCalloutExpert,
  categorySlug,
  analytics,
  swap = false,
  headerClassName
}) => {
  const expertProfilePhoto =
    expertCalloutExpert.expertProfile && expertCalloutExpert.expertProfile.profilePhoto

  if (!expertProfilePhoto) {
    return null
  }

  const averageRating =
    expertCalloutExpert.expertStats && expertCalloutExpert.expertStats.averageRating
  const totalLeadsAsPrimaryExpert =
    expertCalloutExpert.expertStats && expertCalloutExpert.expertStats.totalLeadsAsPrimaryExpert
  const totalReviews =
    expertCalloutExpert.expertStats && expertCalloutExpert.expertStats.totalReviews
  const expertLocation =
    expertCalloutExpert.expertProfile && expertCalloutExpert.expertProfile.location

  return !swap ? (
    <div className={styles.wrapper}>
      <SidebarAwareGrid className={styles.contents}>
        <GridItem col={2} mdCol={5} lgCol={4}>
          <div className={styles.card}>
            <div className={styles.cardTopContent}>
              <Link
                to={`/experts/${expertCalloutExpert.vanityId || expertCalloutExpert.id}`}
                className={styles.actionLight}
              >
                <Image
                  src={expertProfilePhoto.url}
                  sizes="(min-width: 960px) 375px, 150px"
                  className={styles.image}
                />
              </Link>
              <div className={styles.cardCopy}>
                <div className={styles.expertName}>{expertCalloutExpert.displayName}</div>

                {expertCalloutExpert.expertProfile &&
                  expertCalloutExpert.expertProfile.headline && (
                    <div className={styles.expertHeadline}>
                      {expertCalloutExpert.expertProfile.headline}
                    </div>
                  )}
              </div>
            </div>
            <div className={styles.cardHighlights}>
              {totalReviews !== null && totalReviews > 5 && (
                <div className={styles.cardRow}>
                  <Star className={styles.cardIcon} />
                  {numeral(totalReviews).format('0,0')} reviews{' '}
                  {averageRating && averageRating > 4
                    ? ` averaging ${averageRating.toFixed(1)} stars`
                    : ''}
                </div>
              )}

              {totalLeadsAsPrimaryExpert !== null && totalLeadsAsPrimaryExpert > 10 && (
                <div className={styles.cardRow}>
                  <Comment className={styles.cardIcon} />{' '}
                  {numeral(totalLeadsAsPrimaryExpert).format('0,0')} customers helped
                </div>
              )}

              {expertLocation && (
                <div className={styles.cardRow}>
                  <Map className={styles.cardIcon} /> {expertLocation}
                </div>
              )}
            </div>
          </div>
        </GridItem>
        <GridItem col={2} mdCol={7} lgCol={8} className={styles.copy}>
          <div className={classNames(styles.headline, headerClassName)}>
            {placement === ExpertCalloutPlacement.PRODUCT ? (
              <>
                Have questions about this product? Your expert {expertCalloutExpert.shortName} is
                here to help
              </>
            ) : (
              <>Have questions? Ask your expert {expertCalloutExpert.shortName}.</>
            )}
          </div>
          <div className={styles.body}>Connect with your expert and</div>
          <div className={styles.list}>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Get personalized recommendations</div>
            </div>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Discover different options</div>
            </div>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Ask questions anytime</div>
            </div>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Find the best deal for you</div>
            </div>
          </div>
          <div className={styles.actions}>
            <ExpertChatButton
              guestLocation={generateCreateUrlFromCategory({
                maybeCategorySlug: categorySlug,
                sourceKey: 'guest-expert-callout',
                pathIntent: 'request'
              })}
              expertId={expertCalloutExpert.id}
              className={styles.actionDark}
            >
              {(startExpertCustomerConversation, loading) => {
                return (
                  <Button
                    className={styles.chatButton}
                    disabled={loading}
                    variant="secondary"
                    onClick={() => {
                      if (analytics) {
                        analytics.events.expertCalloutChatClicked()
                      }

                      startExpertCustomerConversation ? startExpertCustomerConversation() : null
                    }}
                  >
                    {`Talk to ${expertCalloutExpert.displayName}`}
                  </Button>
                )
              }}
            </ExpertChatButton>

            <Link
              to={`/experts/${expertCalloutExpert.vanityId || expertCalloutExpert.id}`}
              className={styles.actionLight}
            >
              <Button
                className={styles.viewProfile}
                variant="contrast-outline"
                onClick={() => {
                  if (analytics) {
                    analytics.events.viewExpertProfileClicked({
                      business_user_id: expertCalloutExpert.id
                    })
                  }
                }}
              >
                View profile
              </Button>
            </Link>
          </div>
        </GridItem>
      </SidebarAwareGrid>
    </div>
  ) : (
    <div className={styles.wrapper}>
      <SidebarAwareGrid className={styles.contents}>
        <GridItem col={2} mdCol={7} lgCol={8} className={styles.copy}>
          <div className={classNames(styles.headline, headerClassName)}>
            {placement === ExpertCalloutPlacement.PRODUCT ? (
              <>
                Have questions about this product? Your expert {expertCalloutExpert.displayName} is
                here to help
              </>
            ) : (
              <>Have questions? Ask your expert {expertCalloutExpert.displayName}.</>
            )}
          </div>
          <div className={styles.body}>Connect with your expert and</div>
          <div className={styles.list}>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Get personalized recommendations</div>
            </div>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Discover different options</div>
            </div>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Ask questions anytime</div>
            </div>
            <div className={styles.listRow}>
              <div className={styles.icon}>
                <Check />
              </div>
              <div className={styles.listCopy}>Find the best deal for you</div>
            </div>
          </div>
          <div className={styles.actions}>
            <ExpertChatButton
              guestLocation={generateCreateUrlFromCategory({
                maybeCategorySlug: categorySlug,
                sourceKey: 'guest-expert-callout',
                pathIntent: 'request'
              })}
              expertId={expertCalloutExpert.id}
              className={styles.actionDark}
            >
              {(startExpertCustomerConversation, loading) => {
                return (
                  <Button
                    className={styles.chatButton}
                    disabled={loading}
                    variant="secondary"
                    onClick={() => {
                      if (analytics) {
                        analytics.events.expertCalloutChatClicked()
                      }

                      startExpertCustomerConversation ? startExpertCustomerConversation() : null
                    }}
                  >
                    {`Talk to ${expertCalloutExpert.shortName}`}
                  </Button>
                )
              }}
            </ExpertChatButton>

            <Link
              to={`/experts/${expertCalloutExpert.vanityId || expertCalloutExpert.id}`}
              className={styles.actionLight}
            >
              <Button
                className={styles.viewProfile}
                variant="contrast-outline"
                onClick={() => {
                  if (analytics) {
                    analytics.events.viewExpertProfileClicked({
                      business_user_id: expertCalloutExpert.id
                    })
                  }
                }}
              >
                View profile
              </Button>
            </Link>
          </div>
        </GridItem>
        <GridItem col={2} mdCol={5} lgCol={4}>
          <div className={styles.card}>
            <div className={styles.cardTopContent}>
              <Image
                src={expertProfilePhoto.url}
                sizes="(min-width: 960px) 375px, 150px"
                className={styles.image}
              />
              <div className={styles.cardCopy}>
                <div className={styles.expertName}>{expertCalloutExpert.displayName}</div>

                {expertCalloutExpert.expertProfile &&
                  expertCalloutExpert.expertProfile.headline && (
                    <div className={styles.expertHeadline}>
                      {expertCalloutExpert.expertProfile.headline}
                    </div>
                  )}
              </div>
            </div>
            <div className={styles.cardHighlights}>
              {totalReviews !== null && totalReviews > 5 && (
                <div className={styles.cardRow}>
                  <Star className={styles.cardIcon} />
                  {numeral(totalReviews).format('0,0')} reviews{' '}
                  {averageRating && averageRating > 4
                    ? ` averaging ${averageRating.toFixed(1)} stars`
                    : ''}
                </div>
              )}

              {totalLeadsAsPrimaryExpert !== null && totalLeadsAsPrimaryExpert > 10 && (
                <div className={styles.cardRow}>
                  <Comment className={styles.cardIcon} />{' '}
                  {numeral(totalLeadsAsPrimaryExpert).format('0,0')} customers helped
                </div>
              )}

              {expertLocation && (
                <div className={styles.cardRow}>
                  <Map className={styles.cardIcon} /> {expertLocation}
                </div>
              )}
            </div>
          </div>
        </GridItem>
      </SidebarAwareGrid>
    </div>
  )
}

export default withAnalytics(V2)
